import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Checkbox,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";

const PlatformsSelect = ({
  contentType,
  platforms,
  availablePlatforms,
  handlePlatformChange,
  removePlatform,
}) => {
  const allPlatformsSelected =
    availablePlatforms.length > 0 &&
    availablePlatforms.every((p) => platforms.includes(p));

  // Intercept the __select_all_platforms__ sentinel value
  const _handleChange = (e) => {
    const { value } = e.target;
    if (Array.isArray(value) && value.includes("__select_all_platforms__")) {
      handlePlatformChange({
        target: { value: allPlatformsSelected ? [] : availablePlatforms },
      });
    } else {
      handlePlatformChange(e);
    }
  };

  return (
    <>
      {/* Quick Select All / Clear All bar */}
      {availablePlatforms.length > 0 && contentType && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.75,
            px: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
            {platforms.length} / {availablePlatforms.length} selected
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label="Select All"
              size="small"
              icon={<SelectAllIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() =>
                handlePlatformChange({
                  target: { value: allPlatformsSelected ? [] : availablePlatforms },
                })
              }
              disabled={allPlatformsSelected}
              sx={{
                cursor: "pointer",
                bgcolor: allPlatformsSelected ? "#dcfce7" : "#e0e7ff",
                color: allPlatformsSelected ? "#15803d" : "#4B5AB6",
                fontWeight: 600,
                fontSize: "0.72rem",
                "&:hover": { bgcolor: "#c7d2fe" },
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            />
            <Chip
              label="Clear All"
              size="small"
              icon={<ClearAllIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() =>
                handlePlatformChange({ target: { value: [] } })
              }
              disabled={platforms.length === 0}
              sx={{
                cursor: "pointer",
                bgcolor: "#fef2f2",
                color: "#dc2626",
                fontWeight: 600,
                fontSize: "0.72rem",
                "&:hover": { bgcolor: "#fee2e2" },
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            />
          </Box>
        </Box>
      )}

      <FormControl
        fullWidth
        sx={{ mb: 2 }}
        error={platforms.length === 0}
        disabled={!contentType}
      >
        <InputLabel>Platforms *</InputLabel>
        <Select
          multiple
          required
          value={platforms}
          onChange={_handleChange}
          label="Platforms *"
          renderValue={(selected) => (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                flexWrap: "nowrap",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              {selected.slice(0, 2).map((value) => (
                <Box
                  key={value}
                  sx={{ display: "inline-flex", flexShrink: 0 }}
                  onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                >
                  <Chip
                    size="small"
                    label={value}
                    onDelete={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      removePlatform(value);
                    }}
                    deleteIcon={
                      <CloseIcon sx={{ fontSize: "13px !important", cursor: "pointer" }} />
                    }
                    sx={{
                      bgcolor: "#e0e7ff",
                      color: "#4B5AB6",
                      fontSize: "0.72rem",
                      height: 24,
                      "& .MuiChip-deleteIcon": {
                        color: "#4B5AB6",
                        "&:hover": { color: "#ef4444" },
                      },
                    }}
                  />
                </Box>
              ))}
              {selected.length > 2 && (
                <Box
                  sx={{
                    flexShrink: 0,
                    bgcolor: "#4B5AB6",
                    color: "#fff",
                    borderRadius: "12px",
                    px: 0.8,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    lineHeight: "24px",
                    height: 24,
                  }}
                >
                  +{selected.length - 2}
                </Box>
              )}
            </Box>
          )}
          sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
        >
          {availablePlatforms.length > 0 && [
            <MenuItem
              key="__select_all_platforms__"
              value="__select_all_platforms__"
              sx={{ borderBottom: "1px solid #e2e8f0", mb: 0.5 }}
            >
              <Checkbox
                checked={allPlatformsSelected}
                indeterminate={platforms.length > 0 && !allPlatformsSelected}
                sx={{
                  color: "#4B5AB6",
                  "&.Mui-checked": { color: "#4B5AB6" },
                  "&.MuiCheckbox-indeterminate": { color: "#4B5AB6" },
                  p: 0.5,
                  mr: 1,
                }}
              />
              <Typography sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.875rem" }}>
                {allPlatformsSelected
                  ? "Deselect All"
                  : `Select All (${availablePlatforms.length})`}
              </Typography>
            </MenuItem>,
            ...availablePlatforms.map((platform) => (
              <MenuItem key={platform} value={platform}>
                <Checkbox
                  checked={platforms.includes(platform)}
                  sx={{
                    color: "#4B5AB6",
                    "&.Mui-checked": { color: "#4B5AB6" },
                    p: 0.5,
                    mr: 1,
                  }}
                />
                {platform}
              </MenuItem>
            )),
          ]}
        </Select>

        {platforms.length === 0 && (
          <FormHelperText>
            {!contentType
              ? "Please select content type first"
              : "Please select at least one platform"}
          </FormHelperText>
        )}
        {contentType && availablePlatforms.length > 0 && (
          <FormHelperText sx={{ color: "#4B5AB6", fontWeight: 500 }}>
            Available for {contentType}: {availablePlatforms.join(", ")}
          </FormHelperText>
        )}
        {platforms.length > 0 && (
          <FormHelperText sx={{ color: "#059669", fontWeight: 500 }}>
            💡 Click the ✕ icon on any chip to remove it quickly
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
};

export default PlatformsSelect;

